import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Newspaper, Calendar, Store, Home } from "lucide-react";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/community")({
  head: () => ({
    meta: [
      { title: "Community Hub | Trading Card News, Events & Dealers | BidNova Auctions" },
      {
        name: "description",
        content:
          "The BidNova Community Hub — trading card news, buying guides, UK card show calendar and a searchable directory of trusted UK card dealers and hobby shops.",
      },
      { property: "og:title", content: "BidNova Community Hub" },
      {
        property: "og:description",
        content: "UK trading card news, card shows and dealer directory.",
      },
      { property: "og:type", content: "website" },
    ],
  }),
  component: CommunityLayout,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function CommunityLayout() {
  const { t } = useTranslation("community");
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const NAV = [
    { to: "/community", label: t("nav.home", "Community Home"), icon: Home, exact: true },
    { to: "/community/articles", label: t("nav.articles", "News & Articles"), icon: Newspaper, exact: false },
    { to: "/community/events", label: t("nav.events", "Card Shows"), icon: Calendar, exact: false },
    { to: "/community/dealers", label: t("nav.dealers", "Dealer Directory"), icon: Store, exact: false },
  ] as const;
  return (
    <div className="relative">
      <div className="border-b border-white/10 bg-gradient-to-b from-primary/[0.08] via-transparent to-transparent">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 pt-10 pb-6">
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">{t("layout.breadcrumbHome", "Home")}</Link>
            <span className="mx-2">/</span>
            <span className="text-white/80">{t("layout.breadcrumbCommunity", "Community")}</span>
          </nav>
          <div className="mt-3 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-primary">{t("layout.eyebrow", "Community Hub")}</p>
              <h1 className="mt-2 font-display text-3xl sm:text-4xl text-white">
                {t("layout.title", "News, Shows & UK Dealers")}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                {t("layout.subtitle", "Everything happening around the UK trading card scene — curated by BidNova.")}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {NAV.map(({ to, label, icon: Icon, exact }) => {
                const active = exact ? pathname === to : pathname.startsWith(to);
                return (
                  <Link
                    key={to}
                    to={to}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs transition-colors ${
                      active
                        ? "border-primary/50 bg-primary/15 text-white"
                        : "border-white/10 bg-white/[0.03] text-muted-foreground hover:border-primary/40 hover:text-white"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" aria-hidden />
                    {label}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <Outlet />
    </div>
  );
}
